/**
 * Where a U.S. pricing tier's CTA ("Choose Tier 1/2/3") goes.
 *
 * Each tier is bought through a Stripe Payment Link whose URL is set at build time:
 *   VITE_US_CHECKOUT_T1, VITE_US_CHECKOUT_T2, VITE_US_CHECKOUT_T3
 * A link opens in a new tab, like the Singapore Payment Link CTA. Until a link is set (or when it
 * is not an https URL) the CTA falls back to the U.S. product's sign-in (`/us/app`), same tab, so
 * nothing on the page breaks before the links exist.
 *
 * The env is read on every call (not at import), so a test can stub it with vi.stubEnv or pass
 * its own `env` object.
 */
import { usAppHref } from "../../lib/region";

export const US_CHECKOUT_TIERS = [1, 2, 3];

function readCheckoutEnv() {
  try {
    // Spelled out so Vite can replace each one statically in the production build.
    return {
      VITE_US_CHECKOUT_T1: import.meta.env?.VITE_US_CHECKOUT_T1,
      VITE_US_CHECKOUT_T2: import.meta.env?.VITE_US_CHECKOUT_T2,
      VITE_US_CHECKOUT_T3: import.meta.env?.VITE_US_CHECKOUT_T3,
    };
  } catch {
    return {};
  }
}

/** { href, newTab } for tier 1, 2 or 3. */
export function usCheckoutCta(tier, env = readCheckoutEnv()) {
  const url = String(env?.[`VITE_US_CHECKOUT_T${tier}`] ?? "").trim();
  if (/^https:\/\/\S+$/i.test(url)) return { href: url, newTab: true };
  return { href: usAppHref("/"), newTab: false };
}
